import { SubTrigger as RadixSubTrigger } from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import { forwardRef, useContext } from 'react';

import Breakpoints from '@dynatrace/strato-design-tokens/breakpoints-js';
import {
  ChevronDownSmallIcon,
  ChevronRightSmallIcon,
} from '@dynatrace/strato-icons';

import { MenuSubContext } from './contexts/MenuSubContext.js';
import { useIsMenuItemHovered } from './hooks/useIsMenuItemHovered.js';
import { getSubTriggerSlots } from './item-slots.js';
import * as styles from './Menu.sty.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useBreakpoint } from '../../layouts/hooks/useBreakpoint.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';

/**
 * @public
 */
export interface MenuSubTriggerProps extends WithChildren, AriaLabelingProps {
  /**
   * Whether the menu trigger item is disabled.
   * If true, it prevents the user from interacting with the item.
   */
  disabled?: boolean;

  /**
   * Optional text used for typeahead purposes. By default, the typeahead
   * behavior will use the .textContent of the item. Use this when the content
   * is complex, or you have non-textual content inside.
   */
  textValue?: string;
}

/**
 * An item that opens a submenu. Used in combination with a `Menu.Sub`.
 * Must not be rendered inside a root Menu.
 * @public
 */
export const SubTrigger = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuSubTriggerProps
>(
  (
    {
      children,
      disabled,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
      ...remainingProps
    }: MenuSubTriggerProps,
    forwardedRef,
  ) => {
    const slots = getSubTriggerSlots(children);

    const { isOpen, setIsOpen } = useContext(MenuSubContext);
    const isMobile = useBreakpoint(`(max-width: ${Breakpoints.Max.Mobile}px)`);

    const { focusProps, focusClassName } = useFocusRing({
      disabled,
    });
    const { isItemHovered, hoverProps } = useIsMenuItemHovered();

    return (
      <RadixSubTrigger
        ref={forwardedRef}
        disabled={disabled}
        className={clsx(
          textStyleCSS({ textStyle: 'base-emphasized' }),
          !isItemHovered && focusClassName,
          styles.item({ disabled: disabled }),
        )}
        {..._mergeProps(remainingProps, focusProps, hoverProps)}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
        aria-haspopup={true}
        role="menuitem"
        asChild
      >
        <Flex
          justifyContent="space-between"
          onClick={isMobile && isOpen ? () => setIsOpen(false) : undefined}
        >
          {slots.prefix}
          <div className={styles.subChildren}>{slots.freeform}</div>
          {isMobile && isOpen ? (
            <ChevronDownSmallIcon />
          ) : (
            <ChevronRightSmallIcon />
          )}
        </Flex>
      </RadixSubTrigger>
    );
  },
);

(SubTrigger as typeof SubTrigger & { displayName: string }).displayName =
  'Menu.SubTrigger';
